import { ClientType, Party } from '../../common/types/client';
import { DataGridProps, GridColDef } from '@mui/x-data-grid';
import { JSX } from 'react';
import DataGridEditPartyRepresentativeCell from './dataGridEditPartyRepresentativeCell';
import PreloadedClientList from '../../clients/clientList/preloadedClientList';

export const partyRepresentativeValueGetter = (_: unknown, row: Party): string => {
  if (row.partyRoleplayerTypeID === ClientType.Individual) {
    return 'N/A';
  }
  return row.representativeRoleplayerName || 'Choose...';
};

interface PartyListProps<T extends Party> extends Omit<DataGridProps<T>, 'columns' | 'rows'> {
  /**
   * Parties to display
   */
  parties: T[];
  /**
   * Callback for deleting a party
   * If not provided, delete button will not be displayed
   */
  onDelete?: (partyToDelete: T) => void;
  /**
   * Callback for changing a party
   */
  onChange?: (party: T) => void;
}

/**
 * Base component for displaying list of selected parties and allow to delete items and set representative
 */
const PartyList = <T extends Party>({
  parties,
  onDelete,
  onChange,
  ...props
}: PartyListProps<T>): JSX.Element => {
  const isEditable = Boolean(onChange);

  const columns: GridColDef<T>[] = [
    {
      field: 'partyRoleplayerName',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'partyRoleplayerTaxStatusNote',
      headerName: 'Tax status',
      minWidth: 150,
      flex: 1,
    },
    {
      field: 'partyRoleplayerIDReg',
      headerName: 'ID number or registration',
      minWidth: 150,
      flex: 1,
    },
    {
      field: 'representativeRoleplayerID',
      headerName: 'Representative life',
      minWidth: 150,
      flex: 1,
      editable: isEditable,
      valueGetter: partyRepresentativeValueGetter,
      renderEditCell: DataGridEditPartyRepresentativeCell,
    },
    {
      field: 'relationshipNote',
      headerName: 'Relationship',
      minWidth: 150,
      flex: 1,
    },
  ];

  // Generate
  const processRowUpdate = (newRow: T): T => {
    onChange?.(newRow);
    return newRow;
  };

  return (
    <PreloadedClientList<T>
      {...props}
      // Allow to edit representative person only for juristic parties
      isCellEditable={({ row, field }): boolean =>
        field === 'representativeRoleplayerID' && row.partyRoleplayerTypeID === ClientType.Business
      }
      enableQuickFilter={false}
      clients={parties}
      columns={columns}
      processRowUpdate={processRowUpdate}
      onClickDelete={onDelete}
    />
  );
};

export default PartyList;
